import * as React from "react";
import { useId } from "@react-aria/utils";
import AlertOutlined from "@bolteu/kalep-react-icons/dist/AlertOutlined";
import InfoCircleOutlined from "@bolteu/kalep-react-icons/dist/InfoCircleOutlined";
import { Button } from "../Button";
import { cx } from "../../helpers/utils/cx";
const DROP_AREA_DEFAULT_PRIMARY_TEXT = "Drop files here to upload";
const DROP_AREA_DEFAULT_SECONDARY_TEXT = "or choose from your device";
const BROWSE_BUTTON_DEFAULT_TEXT = "Browse";
export function UploadArea(_ref) {
  let {
    onFilesAttached,
    filesAttached = false,
    fullWidth = false,
    disabled = false,
    required = false,
    multiple = false,
    id,
    name,
    label,
    helperText,
    renderDropAreaContent = renderDropAreaContentDefault,
    accept,
    dropAreaPrimaryText = DROP_AREA_DEFAULT_PRIMARY_TEXT,
    dropAreaSecondaryText = DROP_AREA_DEFAULT_SECONDARY_TEXT,
    browseButtonText = BROWSE_BUTTON_DEFAULT_TEXT
  } = _ref;
  const fileInputRef = React.useRef(null);
  const dropTargetRef = React.useRef(null);
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const handleButtonClick = React.useCallback(() => {
    if (disabled) {
      return;
    }
    if (fileInputRef.current) {
      // Reset input.
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  }, [fileInputRef, disabled]);
  const handleInputChange = React.useCallback(event => {
    if (event.target.files) {
      onFilesAttached(toFileArray(event.target.files));
    }
  }, [onFilesAttached]);
  const handleDragOver = React.useCallback(event => {
    event.preventDefault();
    if (disabled) {
      return;
    }
    if (dropTargetRef.current && !dropTargetRef.current.classList.contains("border-positive-primary")) {
      dropTargetRef.current.classList.remove("border-neutral-secondary");
      dropTargetRef.current.classList.add("border-positive-primary");
      dropTargetRef.current.classList.remove("bg-transparent");
      dropTargetRef.current.classList.add("bg-positive-secondary");
    }
  }, [disabled]);
  const handleDragLeave = React.useCallback(event => {
    event.preventDefault();
    if (disabled) {
      return;
    }
    if (dropTargetRef.current && !dropTargetRef.current.classList.contains("border-neutral-secondary")) {
      dropTargetRef.current.classList.add("border-neutral-secondary");
      dropTargetRef.current.classList.remove("border-positive-primary");
      dropTargetRef.current.classList.add("bg-transparent");
      dropTargetRef.current.classList.remove("bg-positive-secondary");
    }
  }, [disabled]);
  const handleDrop = React.useCallback(event => {
    event.preventDefault();
    if (disabled) {
      return;
    }
    if (event.dataTransfer.files) {
      onFilesAttached(toFileArray(event.dataTransfer.files));
    }

    // handle the case when dragleave is not naturally triggered and drop target is still highlighted
    handleDragLeave(event);
  }, [onFilesAttached, disabled, handleDragLeave]);
  return /*#__PURE__*/React.createElement("div", {
    className: cx("flex flex-col gap-2")
  }, label && /*#__PURE__*/React.createElement("label", {
    htmlFor: inputId,
    className: cx("w-fit bolt-font-body-s-accent", disabled ? " text-tertiary" : "text-primary", required && "after:text-danger-primary after:content-['_*']")
  }, label), /*#__PURE__*/React.createElement("div", {
    className: cx("border-2 border-dashed border-neutral-secondary rounded", "flex gap-4 justify-between items-center", "bg-special-nulled", filesAttached ? "flex-row p-3" : "flex-col px-10 py-6", fullWidth ? "w-full" : "w-96", disabled && "cursor-not-allowed"),
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
    ref: dropTargetRef
  }, renderDropAreaContent({
    onFilesAttached,
    filesAttached,
    fullWidth,
    disabled,
    id,
    name,
    label,
    dropAreaPrimaryText,
    dropAreaSecondaryText
  }), /*#__PURE__*/React.createElement("input", {
    id: inputId,
    name: name,
    type: "file",
    disabled: disabled,
    onChange: handleInputChange,
    className: cx("hidden"),
    ref: fileInputRef,
    multiple: multiple,
    accept: accept
  }), /*#__PURE__*/React.createElement(Button, {
    variant: "secondary",
    size: "sm",
    onClick: handleButtonClick,
    disabled: disabled
  }, browseButtonText)), helperText && /*#__PURE__*/React.createElement("div", {
    className: cx("bolt-font-body-s-regular text-secondary", disabled && "text-tertiary", {
      /* error && "text-danger-primary", */
    })
  }, helperText));
}
function renderDropAreaContentDefault(props) {
  return /*#__PURE__*/React.createElement("div", {
    className: cx("flex flex-col")
  }, /*#__PURE__*/React.createElement("span", {
    className: cx("bolt-font-body-m-regular", props.disabled ? "text-secondary" : "text-primary")
  }, props.dropAreaPrimaryText), /*#__PURE__*/React.createElement("span", {
    className: cx("bolt-font-body-s-regular", props.disabled ? "text-tertiary" : "text-secondary")
  }, props.dropAreaSecondaryText));
}
function toFileArray(files) {
  return Array.from(files);
}
export function UploadHelperText(_ref2) {
  let {
    disabled,
    appearance = "default",
    children
  } = _ref2;
  if (!children) {
    return null;
  }
  return /*#__PURE__*/React.createElement("div", {
    className: cx("bolt-font-body-s-regular text-secondary", "flex items-center gap-2 mt-1", disabled && "text-tertiary", appearance === "error" && "text-danger-primary")
  }, appearance === "info" && /*#__PURE__*/React.createElement(InfoCircleOutlined, {
    size: "sm"
  }), appearance === "error" && /*#__PURE__*/React.createElement(AlertOutlined, {
    size: "sm"
  }), children);
}